import { HardDrive, Layers2, LayoutGrid, ListMusic } from "lucide-react";

import { Card, Progress, StatCard, StatStrip } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import { formatByteSize } from "@/music-catalog/utils/catalog-view";

/**
 * What the account holds, above the list of it.
 *
 * The storage figure is drawn against the limit the API sends rather than one
 * written down here: a client that has to know the quota and is not told writes
 * it down, which is how the desktop came to carry its own copy of the number.
 */
export const CatalogSummary = ({
  duplicateGroupCount,
  projectCount,
  storageLimitBytes,
  storedBytes,
  trackCount,
}: {
  duplicateGroupCount: number;
  projectCount: number;
  storageLimitBytes: number;
  storedBytes: number;
  trackCount: number;
}) => {
  const { language, t } = useTranslation();
  const storedPercent =
    storageLimitBytes > 0 ? Math.min(100, (storedBytes / storageLimitBytes) * 100) : 0;

  return (
    <>
      {/* One strip divided by hairlines, as the desktop draws its summaries:
          a summary is one thing with three numbers in it, not three cards. */}
      <StatStrip className="sm:grid-cols-3 xl:grid-cols-3">
        <StatCard
          icon={<LayoutGrid />}
          label={t("catalog.projects")}
          value={String(projectCount)}
        />
        <StatCard icon={<ListMusic />} label={t("catalog.tracks")} value={String(trackCount)} />
        {/*
          A duplicate is Layers2 wherever it is counted or labelled, and the
          tile is toned only when there is one to find: a permanent amber tile
          is a warning about nothing.
        */}
        <StatCard
          icon={<Layers2 />}
          label={t("catalog.duplicates")}
          value={String(duplicateGroupCount)}
          tone={duplicateGroupCount > 0 ? "warning" : undefined}
        />
      </StatStrip>

      <Card className="flex flex-col gap-2 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-sm font-medium">
            <HardDrive className="size-4 text-muted-foreground" aria-hidden />
            {t("catalog.stored")}
          </span>
          <span className="text-sm text-muted-foreground">
            {t("catalog.storedOf", {
              limit: formatByteSize(storageLimitBytes, language),
              used: formatByteSize(storedBytes, language),
            })}
          </span>
        </div>
        <Progress value={storedPercent} label={t("catalog.stored")} />
      </Card>
    </>
  );
};
